import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  networkApi,
  RECALC_METRICS,
  type EditableField,
  type NetworkReport,
  type NetworkRow,
  type RecalcMetric,
} from '../../api/network.js';
import { EmptyBox, ErrorBox, SafeLink, Spinner } from '../../components/ui.js';
import { useUrlUpdate } from '../../lib/hooks.js';

/**
 * 네트워크 광고 현황 — 레거시 /xadm/report_network_test_frame_main.php(왼쪽 그룹
 * 목록) + report_network_test_frame.php(표). 원형은 report_network.php 다.
 *
 * 클릭·건수·구매금액·가입수는 DB 에 저장된 값이고, [up] 을 눌러야 원천에서 다시
 * 계산된다. 원본처럼 0 으로 계산된 값은 저장하지 않는다(이전 값이 남는다).
 *
 * 원본과 달라진 점:
 *  - [allup]·[all] 은 요청 하나로 서버가 한 번에 계산한다(원본은 행 × 5개 요청을
 *    동시에 보내다 서버 500 이 났다).
 *  - 그룹은 하나만 고르고 구분선은 고를 수 없다(원본은 그 이름으로 빈 행이 생겼다).
 *  - 입력칸을 숨기면 메모 칸에 메모가 보인다(원본은 노출수를 찍었다 - 2026-09-11 수정).
 */

const DEFAULT_GROUP = 'popunder';
const WEEKDAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'] as const;

/** 원본 :361 - 그룹명을 누르면 파트너 사이트의 광고 리포트가 새 창으로 열렸다. */
const AD_REPORT_URL = 'http://partner.bananamall.co.kr/?html=AD_Report&pr_part=';

/** 원본은 광고비나 구매금액이 0 이면 수익율 자리에 이 글자를 찍었다 (:428, :519). */
const NO_PROFIT = '1,000%';

const FIELD_LABEL: Record<EditableField, string> = { cpc: '지출비용', imp: '노출수', memo: '메모' };

const METRIC_LABEL: Record<RecalcMetric, string> = {
  clickp: '클릭(P)',
  clickm: '클릭(M)',
  ordernum: '건수',
  price: '구매금액',
  joinnum: '가입수',
};

/** PHP number_format($n) — 소수점 없이 반올림(0.5 는 0 에서 먼 쪽)하고 천 단위 쉼표. */
export function numberFormat(value: number): string {
  const scaled = Number(value.toPrecision(15));
  const rounded = Math.sign(scaled) * Math.round(Math.abs(scaled));
  return (rounded === 0 ? 0 : rounded).toLocaleString('en-US');
}

/** 원본은 계산한 비율을 그대로 echo 하고 '%' 를 붙였다 (예: 12.5%). */
function percent(value: number): string {
  return `${value}%`;
}

type CellKey = `${number}:${EditableField}`;

export function NetworkReportPage() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const update = useUrlUpdate(params, setParams);

  const group = params.get('group') || DEFAULT_GROUP;
  const from = params.get('from') || undefined;
  const to = params.get('to') || undefined;
  const weekdayParam = params.get('weekday');
  const weekday = weekdayParam !== null && /^[0-6]$/.test(weekdayParam) ? Number(weekdayParam) : undefined;

  /** 원본 '입력view' / '입력hide'. */
  const [showInputs, setShowInputs] = useState(true);
  /** 원본 'up숨기기'. */
  const [showUp, setShowUp] = useState(true);
  const [fromDraft, setFromDraft] = useState<string | null>(null);
  const [toDraft, setToDraft] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Partial<Record<CellKey, string>>>({});
  const [savedCells, setSavedCells] = useState<ReadonlySet<CellKey>>(new Set());
  const [notice, setNotice] = useState<string | null>(null);

  const groups = useQuery({ queryKey: ['network', 'groups'], queryFn: networkApi.groups, staleTime: Infinity });

  // 원본은 화면을 여는 GET 안에서 오늘·어제 행을 만들었다(:7-37). 여기서는 그룹이
  // 바뀔 때 POST 로 한 번 부르고, 그게 끝난 뒤에 표를 읽는다 - 순서가 바뀌면 방금
  // 만든 오늘 행이 첫 표에 안 보인다.
  const ensured = useQuery({
    queryKey: ['network', 'ensure', group],
    queryFn: () => networkApi.ensureDates(group),
    staleTime: 5 * 60_000,
    retry: false,
  });

  const report = useQuery({
    queryKey: ['network', 'report', group, from, to, weekday],
    queryFn: () => networkApi.report({ group, from, to, weekday }),
    enabled: ensured.isFetched,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ['network', 'report'] });

  const recalc = useMutation({
    mutationFn: (v: { seqs: number[]; metrics?: RecalcMetric[] }) => networkApi.recalc(v.seqs, v.metrics),
    onSuccess: (data, v) => {
      const wanted: readonly RecalcMetric[] = v.metrics ?? RECALC_METRICS;
      const unsaved = data.rows.some((r) => wanted.some((m) => !r.saved.includes(m)));
      setNotice(unsaved ? '0으로 계산된 값은 저장하지 않고 이전 값을 그대로 둡니다 (원본과 같은 규칙).' : null);
      void refresh();
    },
  });

  const save = useMutation({
    mutationFn: (v: { seq: number; field: EditableField; value: string }) => networkApi.save(v.seq, v.field, v.value),
    onSuccess: (_data, v) => {
      const key: CellKey = `${v.seq}:${v.field}`;
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setSavedCells((prev) => new Set(prev).add(key));
      void refresh();
    },
  });

  const addPreviousDay = useMutation({
    mutationFn: (date: string) => networkApi.addPreviousDay(group, date),
    onSuccess: (data) => {
      setNotice(data.created ? `${data.date} 행을 만들었습니다.` : `${data.date} 행이 이미 있습니다.`);
      if (data.created) void refresh();
    },
  });

  function clearLocal() {
    setDrafts({});
    setSavedCells(new Set());
    setNotice(null);
  }

  function selectGroup(key: string) {
    // 원본 linkgo(:151-157) - 그룹과 기간만 넘기고 요일은 풀린다.
    clearLocal();
    update({ group: key, weekday: undefined });
  }

  function search(e: FormEvent) {
    e.preventDefault();
    update({ from: fromDraft ?? from, to: toDraft ?? to });
    setFromDraft(null);
    setToDraft(null);
  }

  function reset() {
    // 원본 '초기화' - 파라미터 없이 다시 연다(팝언더, 최근 30일, 전체 요일).
    clearLocal();
    setFromDraft(null);
    setToDraft(null);
    setParams(new URLSearchParams(), { replace: true });
  }

  const pending = recalc.isPending ? recalc.variables : undefined;
  const isCalculating = (seq: number, metric: RecalcMetric) =>
    pending !== undefined && pending.seqs.includes(seq) && (!pending.metrics || pending.metrics.includes(metric));

  function saveCell(row: NetworkRow, field: EditableField) {
    const key: CellKey = `${row.seq}:${field}`;
    const current = field === 'memo' ? row.memo : String(row[field]);
    save.mutate({ seq: row.seq, field, value: drafts[key] ?? current });
  }

  const data = report.data;
  const busy = recalc.isPending;

  return (
    <section>
      <header className="page-head">
        <div>
          <h1 className="page-title">
            네트워크 광고 현황
            {busy && <span className="page-head__busy">원천 데이터에서 다시 계산하는 중…</span>}
          </h1>
          <p className="page-desc">
            클릭·건수·구매금액·가입수는 저장된 값입니다. [up] 을 누르면 원천에서 다시 계산해 저장하고, 0 으로
            계산된 값은 저장하지 않습니다. 광고비는 팝언더·랜딩1·2 가 클릭 × 0.7, 그 외는 지출비용(달러) × 환율입니다.
          </p>
        </div>
      </header>

      <div className="network-layout">
        <aside className="panel network-groups">
          <label className="network-groups__label" htmlFor="network-group-list">
            광고 그룹
          </label>
          {groups.isPending ? (
            <Spinner />
          ) : groups.isError ? (
            <ErrorBox error={groups.error} onRetry={() => void groups.refetch()} />
          ) : (
            <select
              id="network-group-list"
              className="network-groups__list"
              size={28}
              value={group}
              onChange={(e) => selectGroup(e.target.value)}
            >
              {groups.data.groups.map((g) => (
                <option key={g.key} value={g.key} disabled={g.separator === true}>
                  {g.title}
                </option>
              ))}
            </select>
          )}
        </aside>

        <div className="panel network-main">
          <form className="network-filters" onSubmit={search}>
            <label className="network-filters__field">
              일자
              <input
                type="date"
                className="input"
                aria-label="시작일"
                value={fromDraft ?? from ?? data?.from ?? ''}
                onChange={(e) => setFromDraft(e.target.value)}
              />
            </label>
            <span aria-hidden="true">~</span>
            <input
              type="date"
              className="input"
              aria-label="종료일"
              value={toDraft ?? to ?? data?.to ?? ''}
              onChange={(e) => setToDraft(e.target.value)}
            />
            <button className="btn" type="submit">
              검색
            </button>
            <span className="network-filters__rate">기준 환율 {numberFormat(data?.usdToKrw ?? 1300)}원</span>
          </form>

          <div className="toolbar network-toolbar">
            <button type="button" className="btn btn--ghost btn--sm" onClick={reset}>
              초기화
            </button>
            <div className="tab-group" aria-label="요일">
              <button
                type="button"
                className={weekday === undefined ? 'tab-btn tab-btn--active' : 'tab-btn'}
                onClick={() => update({ weekday: undefined })}
              >
                전체
              </button>
              {WEEKDAY_LABELS.map((label, i) => (
                <button
                  key={label}
                  type="button"
                  className={weekday === i ? 'tab-btn tab-btn--active' : 'tab-btn'}
                  onClick={() => update({ weekday: i })}
                >
                  {label}
                </button>
              ))}
            </div>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => setShowInputs((v) => !v)}>
              {showInputs ? '입력칸 숨기기' : '입력칸 보기'}
            </button>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => setShowUp((v) => !v)}>
              {showUp ? '[up] 숨기기' : '[up] 보기'}
            </button>
          </div>

          {notice && <p className="panel__note">{notice}</p>}
          <ErrorBox error={recalc.error} />
          <ErrorBox error={save.error} />
          <ErrorBox error={addPreviousDay.error} />
          {ensured.isError && report.isSuccess && <ErrorBox error={ensured.error} />}

          {!ensured.isFetched || report.isPending ? (
            <Spinner />
          ) : report.isError ? (
            <ErrorBox error={report.error} onRetry={() => void report.refetch()} />
          ) : (
            <ReportTable
              data={report.data}
              showInputs={showInputs}
              showUp={showUp}
              busy={busy}
              drafts={drafts}
              savedCells={savedCells}
              isCalculating={isCalculating}
              onRecalc={(seqs, metrics) => recalc.mutate({ seqs, metrics })}
              onAddPreviousDay={(date) => addPreviousDay.mutate(date)}
              onDraft={(key, value) => setDrafts((prev) => ({ ...prev, [key]: value }))}
              onSave={saveCell}
              saving={save.isPending}
            />
          )}
        </div>
      </div>
    </section>
  );
}

interface ReportTableProps {
  data: NetworkReport;
  showInputs: boolean;
  showUp: boolean;
  busy: boolean;
  drafts: Partial<Record<CellKey, string>>;
  savedCells: ReadonlySet<CellKey>;
  isCalculating: (seq: number, metric: RecalcMetric) => boolean;
  onRecalc: (seqs: number[], metrics?: RecalcMetric[]) => void;
  onAddPreviousDay: (date: string) => void;
  onDraft: (key: CellKey, value: string) => void;
  onSave: (row: NetworkRow, field: EditableField) => void;
  saving: boolean;
}

function ReportTable(props: ReportTableProps) {
  const { data, showInputs, showUp, busy, isCalculating, onRecalc } = props;
  const { rows, totals, randing } = data;

  function metricCell(row: NetworkRow, metric: RecalcMetric, value: number) {
    return (
      <td className="num">
        {isCalculating(row.seq, metric) ? '계산 중' : numberFormat(value)}
        {showUp && (
          <button
            type="button"
            className="network-mini"
            aria-label={`${row.date} ${METRIC_LABEL[metric]} 재계산`}
            disabled={busy}
            onClick={() => onRecalc([row.seq], [metric])}
          >
            [up]
          </button>
        )}
      </td>
    );
  }

  function editCell(row: NetworkRow, field: EditableField) {
    const key: CellKey = `${row.seq}:${field}`;
    const draft = props.drafts[key];
    const stored = field === 'memo' ? row.memo : String(row[field]);
    return (
      <span className="network-edit">
        <input
          className={field === 'memo' ? 'network-edit__memo' : undefined}
          data-saved={props.savedCells.has(key) && draft === undefined ? 'true' : undefined}
          aria-label={`${row.date} ${FIELD_LABEL[field]}`}
          value={draft ?? stored}
          onChange={(e) => props.onDraft(key, e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              props.onSave(row, field);
            }
          }}
        />
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          aria-label={`${row.date} ${FIELD_LABEL[field]} 저장`}
          disabled={props.saving}
          onClick={() => props.onSave(row, field)}
        >
          S
        </button>
      </span>
    );
  }

  return (
    <>
      <h2 className="network-title">{data.group.title}</h2>
      {rows.length === 0 ? (
        <EmptyBox>조건에 맞는 행이 없습니다.</EmptyBox>
      ) : (
        <div className="table-wrap">
          <table className="table network-table">
            <thead>
              <tr>
                <th>일자</th>
                <th>요일</th>
                <th>
                  종류{' '}
                  <button
                    type="button"
                    className="network-mini"
                    aria-label="전체 행 재계산"
                    disabled={busy}
                    onClick={() => onRecalc(rows.map((r) => r.seq))}
                  >
                    [all]
                  </button>
                </th>
                <th className="num">클릭(합)</th>
                <th className="num">클릭(P)</th>
                {randing && <th className="num">클릭율(P)</th>}
                <th className="num">클릭(M)</th>
                {randing && <th className="num">클릭율(M)</th>}
                <th className="num">건수(P+M)</th>
                <th className="num">구매금액</th>
                <th className="num">광고비</th>
                <th className="num">수익율</th>
                <th className="num">클릭율(전체)</th>
                <th className="num">가입수</th>
                <th className="num">지출비용(달러)</th>
                <th className="num">노출수</th>
                <th>메모</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.seq}>
                  <td>
                    {row.date}
                    <button
                      type="button"
                      className="network-mini"
                      aria-label={`${row.date} 전날 행 추가`}
                      onClick={() => props.onAddPreviousDay(row.date)}
                    >
                      [ v ]
                    </button>
                  </td>
                  <td className={row.weekday === 0 ? 'network-sun' : row.weekday === 6 ? 'network-sat' : undefined}>
                    {WEEKDAY_LABELS[row.weekday] ?? ''}
                  </td>
                  <td>
                    <SafeLink href={`${AD_REPORT_URL}${encodeURIComponent(row.groupname)}`}>{row.groupname}</SafeLink>
                    {showUp && (
                      <button
                        type="button"
                        className="network-mini"
                        aria-label={`${row.date} 전체 재계산`}
                        disabled={busy}
                        onClick={() => onRecalc([row.seq])}
                      >
                        [allup]
                      </button>
                    )}
                  </td>
                  <td className="num">{numberFormat(row.clickSum)}</td>
                  {metricCell(row, 'clickp', row.clickp)}
                  {randing && <td className="num">{percent(row.randingRateP ?? 0)}</td>}
                  {metricCell(row, 'clickm', row.clickm)}
                  {randing && <td className="num">{percent(row.randingRateM ?? 0)}</td>}
                  {metricCell(row, 'ordernum', row.ordernum)}
                  {metricCell(row, 'price', row.price)}
                  <td className="num">{numberFormat(row.adCost)}</td>
                  <td className="num">{row.profitRate === null ? NO_PROFIT : percent(row.profitRate)}</td>
                  <td className="num">{percent(row.clickRate)}</td>
                  {metricCell(row, 'joinnum', row.joinnum)}
                  <td className="num">
                    {row.cpcDisplay}
                    {showInputs && editCell(row, 'cpc')}
                  </td>
                  <td className="num">{showInputs ? editCell(row, 'imp') : numberFormat(row.imp)}</td>
                  <td>{showInputs ? editCell(row, 'memo') : row.memo}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="network-total">
                <td colSpan={3}>합계</td>
                <td className="num">{numberFormat(totals.clickSum)}</td>
                <td className="num">{numberFormat(totals.clickp)}</td>
                {randing && <td />}
                <td className="num">{numberFormat(totals.clickm)}</td>
                {randing && <td />}
                <td className="num">{numberFormat(totals.ordernum)}</td>
                <td className="num">{numberFormat(totals.price)}</td>
                <td className="num">{numberFormat(totals.adCost)}</td>
                {/* 합계 수익율·지출비용은 원본처럼 소수점 없이 보인다 (:517, :525). */}
                <td className="num">{totals.profitRate === null ? NO_PROFIT : `${numberFormat(totals.profitRate)}%`}</td>
                <td />
                <td className="num">{numberFormat(totals.joinnum)}</td>
                <td className="num">{numberFormat(totals.cpc)}</td>
                <td className="num">{numberFormat(totals.imp)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </>
  );
}
